import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { generateWeekOptions, type WeekOption } from '@/utils/weekOptions';
import { useTimezoneUtils } from '@/hooks/useTimezoneUtils';
import { useMemo } from 'react';

export type DateMode = 'date' | 'week';

interface ExpenseDateFieldProps {
  dateMode: DateMode;
  onDateModeChange: (mode: DateMode) => void;
  startDate: string;
  onStartDateChange: (date: string) => void;
  selectedWeek: string;
  onSelectedWeekChange: (week: string) => void;
  showEndDate?: boolean;
  endDate?: string;
  onEndDateChange?: (date: string) => void;
  compact?: boolean;
}

export const ExpenseDateField = ({
  dateMode,
  onDateModeChange,
  startDate,
  onStartDateChange,
  selectedWeek,
  onSelectedWeekChange,
  showEndDate = false,
  endDate = '',
  onEndDateChange,
  compact = false,
}: ExpenseDateFieldProps) => {
  const { timezone } = useTimezoneUtils();
  
  const weekOptions = useMemo(() => generateWeekOptions(timezone), [timezone]);
  
  const handleWeekChange = (value: string) => {
    const selectedOption = weekOptions.find(w => w.value === value);
    if (selectedOption) {
      onSelectedWeekChange(value);
      onStartDateChange(selectedOption.startDate);
    }
  };

  return (
    <div className={compact ? 'space-y-2' : 'space-y-3'}>
      <div className="flex items-center gap-4">
        <Label className="text-xs text-muted-foreground">When:</Label>
        <RadioGroup
          value={dateMode}
          onValueChange={(v) => onDateModeChange(v as DateMode)}
          className="flex items-center gap-4"
        >
          <div className="flex items-center gap-1.5">
            <RadioGroupItem value="date" id="date-mode" className="h-3.5 w-3.5" />
            <Label htmlFor="date-mode" className="text-sm cursor-pointer">Date</Label>
          </div>
          <div className="flex items-center gap-1.5">
            <RadioGroupItem value="week" id="week-mode" className="h-3.5 w-3.5" />
            <Label htmlFor="week-mode" className="text-sm cursor-pointer">Week</Label>
          </div>
        </RadioGroup>
      </div>
      
      <div className="flex items-center gap-3">
        {dateMode === 'date' ? (
          <div className="flex-1">
            <Input
              type="date"
              value={startDate}
              onChange={(e) => onStartDateChange(e.target.value)}
              className="h-9"
            />
          </div>
        ) : (
          <div className="flex-1">
            <Select value={selectedWeek} onValueChange={handleWeekChange}>
              <SelectTrigger className="h-9">
                <SelectValue placeholder="Select week..." />
              </SelectTrigger>
              <SelectContent>
                {weekOptions.map((week) => (
                  <SelectItem key={week.value} value={week.value}>
                    {week.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        
        {showEndDate && onEndDateChange && (
          <div className="flex items-center gap-2">
            <Label className="text-xs text-muted-foreground whitespace-nowrap">to</Label>
            <Input
              type="date"
              value={endDate}
              onChange={(e) => onEndDateChange(e.target.value)}
              className="h-9"
              placeholder="End date (optional)"
            />
          </div>
        )}
      </div>
    </div>
  );
};
